import { COOKIE_ERROR_MESSAGE } from '../../constants/Common';

import { Steps } from '../../site/components/container/AuthFunctional/Constants';

import {
    IGoogleAuthResult,
    IGoogleCredentialResponse,
    IGoogleOneTapConfig
} from './types';

import AuthService from '../Api/Auth';
import { getComplement } from '../Lang/getLocaleInfo';
import Url from '../Url';
import Utils from '../Utils';

const GSI_SCRIPT_SRC = 'https://accounts.google.com/gsi/client';

const HTTP_UNAUTHORIZED = 401;

/** Mesmos valores do servidor (`app/Http/Domain/Metric.php`) para cair no bucket de auth. */
const METRIC_FEATURE = 'authentication';
const METRIC_CATEGORY = 'google-authentication-error';
const METRIC_ACTION_CLIENT_ERROR = 'one-tap-client-error';
const METRIC_ACTION_HTTP_ERROR = 'one-tap-http-error';

const sendErrorMetric = (action: string, value = ''): void => {
    window.MetricsService?.setMetricsWithoutJWT('', METRIC_FEATURE, METRIC_CATEGORY, action, value);
};

const loadGsiScript = (): Promise<void> =>
    new Promise((resolve, reject) => {
        const script = document.createElement('script');

        script.src = GSI_SCRIPT_SRC;
        script.async = true;
        script.defer = true;
        script.onload = () => resolve();
        script.onerror = () => reject(new Error('Google Identity Services script failed to load'));

        document.head.appendChild(script);
    });

/**
 * Decide se o convite pode ser inicializado: exige client_id, sessão ausente e navegador
 * fora da webview do app.
 */
export const isOneTapAllowed = (config: IGoogleOneTapConfig): boolean =>
    Boolean(config.clientId) && !Utils.isApp() && !Utils.isLoggedIn();

const establishSession = (astToken: string): void => {
    const params = {
        set_token_js: true,
        ast_token: astToken,
        redirect_to: window.location.href
    };

    Url.location(Url.base(`auth/validate${Url.queryParams(params)}`));
};

const AUTH_FUNCTIONAL_TAG = 'auth-functional';

/**
 * Monta um host próprio para o erro, oculto e fora do fluxo da página.
 *
 * O popup sai por portal para o body (`StyledCommon/Modal.jsx:186`), então ele aparece com o host
 * escondido: o usuário vê só o erro, nunca um formulário de login que não pediu.
 */
const mountOwnAuthHost = (moduleUrl: string): void => {
    const host = document.createElement(AUTH_FUNCTIONAL_TAG);

    host.dataset.initialStep = Steps.LOGIN;
    host.style.display = 'none';
    document.body.appendChild(host);

    // recarregar o mesmo `src` não reexecuta um módulo ESM: com ele já na página, monta na mão
    if (typeof document.renderAuth === 'function') {
        document.renderAuth(host);
        return;
    }

    const script = document.createElement('script');

    script.type = 'module';
    script.src = moduleUrl;
    document.body.appendChild(script);
};

/**
 * Exibe a falha onde o usuário está, com árvore PRÓPRIA.
 *
 * Reaproveitar um `<auth-functional>` da página não funciona: qual deles existe, e onde ele mora,
 * muda por página. No artigo do legado o único com `data-listen-to` é injetado dentro do
 * `dangerouslySetInnerHTML` de outra raiz React (`Modal.jsx:119`), onde um segundo root não
 * monta — o remonte não renderizava nada, não consumia a mensagem, e o erro só aparecia no
 * carregamento seguinte, quando a árvore inline da página lia o cookie sozinha.
 *
 * Quem inicia a tentativa exibe o próprio erro. De quebra, nenhuma árvore da página é remontada —
 * o formulário que o usuário estava preenchendo não é resetado por uma falha do One Tap.
 */
const showAuthError = (fallbackUrl: string, authModuleUrl = ''): void => {
    // o popup vive do cookie/meta: sem mensagem, montar abriria formulário sem explicação
    const hasMessage = document.cookie.includes(`${COOKIE_ERROR_MESSAGE}=`)
        || Boolean(document.querySelector(`meta[name=${COOKIE_ERROR_MESSAGE}]`));

    if (!hasMessage) return;

    // sem o módulo na página o host precisa da URL para buscá-lo
    if (typeof document.renderAuth === 'function' || authModuleUrl) {
        mountOwnAuthHost(authModuleUrl);
        return;
    }

    // defesa em profundidade: o corpo do 401 é uma URL que o servidor montou a partir do
    // `userReference` mandado por este cliente
    if (fallbackUrl.startsWith(Url.base(''))) Url.location(fallbackUrl);
};

const handleCredential = async (
    { credential }: IGoogleCredentialResponse,
    authModuleUrl: string
): Promise<void> => {
    const $AuthService = new AuthService();

    try {
        const { ok, status, body }: IGoogleAuthResult = await $AuthService.googleAuth(credential);

        if (ok && body) {
            establishSession(body);
            return;
        }

        // só o 401 traz URL no corpo
        if (status === HTTP_UNAUTHORIZED) {
            showAuthError(body, authModuleUrl);
            return;
        }

        // 5xx e manutenção trazem HTML: sem URL para navegar e sem cookie para exibir, a falha
        // existe apenas na métrica
        sendErrorMetric(METRIC_ACTION_HTTP_ERROR, String(status));
        showAuthError('', authModuleUrl);
    } catch (error) {
        sendErrorMetric(METRIC_ACTION_CLIENT_ERROR);
        showAuthError('', authModuleUrl);
    }
};

/**
 * Carrega o SDK do Google Identity Services e exibe o convite do One Tap, trocando a credencial
 * por sessão em `auth/validate` e devolvendo o usuário para a página de origem.
 *
 * @example
 * initGoogleOneTap({ clientId });
 */
const initGoogleOneTap = async (config: IGoogleOneTapConfig): Promise<void> => {
    if (!isOneTapAllowed(config)) return;

    try {
        await loadGsiScript();
    } catch (error) {
        return;
    }

    const googleId = window.google?.accounts.id;

    if (!googleId) return;

    googleId.initialize({
        client_id: config.clientId,
        locale: getComplement(Utils.getLang())?.locale || 'pt-BR',
        callback: (response) => {
            handleCredential(response, config.authModuleUrl || '');
        }
    });

    googleId.prompt();
};

export default initGoogleOneTap;
